import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useBlogPosts } from '../../hooks/useBlogPosts';
import { Card, CardContent } from '../../components/ui/card';
import { Calendar, User, Tag, ArrowLeft, Share2, Clock } from 'lucide-react';
import { sanitizeHTML } from '../../utils/contentSanitizer';

const BlogPost = () => {
  const { slug } = useParams();
  const { fetchPost, error } = useBlogPosts();
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [readingTime, setReadingTime] = useState(0);

  useEffect(() => {
    let cancelled = false;
    
    const loadPost = async () => {
      if (!slug) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const fetchedPost = await fetchPost(slug, true);
        
        if (!cancelled && fetchedPost) {
          setPost(fetchedPost);
        }
      } catch (err) {
        console.error('BlogPost: Error loading post:', err);
        if (!cancelled) {
          setPost(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadPost();

    return () => {
      cancelled = true;
    };
  }, [slug, fetchPost]);

  useEffect(() => {
    if (post?.content) {
      // Calculate reading time (average 200 words per minute)
      const text = post.content.replace(/<[^>]*>/g, '');
      const words = text.split(/\s+/).length;
      setReadingTime(Math.ceil(words / 200));
    }
  }, [post]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        // Share cancelled or failed — no action needed
      }
    } else {
      // Fallback to copying URL to clipboard
      navigator.clipboard.writeText(window.location.href);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="h-10 w-32 bg-muted rounded mb-8"></div>
            <div className="h-12 w-3/4 bg-muted rounded mb-4"></div>
            <div className="h-6 w-1/2 bg-muted rounded mb-8"></div>
            <div className="aspect-video bg-muted rounded-lg mb-8"></div>
            <div className="space-y-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-4 bg-muted rounded w-full"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto text-center">
          <Card>
            <CardContent className="p-12">
              <div className="text-destructive text-lg mb-2">Error loading blog post</div>
              <p className="text-muted-foreground mb-6">{error}</p>
              <Link
                to="/blog"
                className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Blog
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Not found or not published (but only redirect if we're not loading)
  if (!isLoading && (!post || post.status !== 'published')) {
    return <Navigate to="/blog" replace />;
  }

  const pageTitle = post ? `${post.title} | Coooly Blog` : 'Coooly Blog';
  const pageDesc = post?.excerpt || 'Insights and guides for dental hygienists and therapists from the Coooly team.';
  const pageUrl = `https://coooly.co.uk/blog/${post?.slug || ''}`;
  const pageImage = post?.featuredImage || 'https://coooly.co.uk/logo512.png';

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={pageImage} />
        <meta property="og:site_name" content="Coooly" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content={pageImage} />
        {post?.publishedAt && <meta property="article:published_time" content={new Date(post.publishedAt).toISOString()} />}
        {post?.author?.name && <meta property="article:author" content={post.author.name} />}
      </Helmet>
      <article className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <div className="mb-8">
            <Link
              to="/blog"
              className="inline-flex items-center text-purple-600 hover:text-purple-800 transition-colors font-medium"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Blog
            </Link>
          </div>

          {/* Article Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-blue-600 text-white p-8">
              {/* Categories */}
              {post.categories && post.categories.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {post.categories.map((category, index) => (
                    <span
                      key={index}
                      className="inline-block px-3 py-1 bg-white/20 text-white rounded-full text-sm font-medium"
                    >
                      {category}
                    </span>
                  ))}
                </div>
              )}

              {/* Title */}
              <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-4">
                {post.title}
              </h1>

              {/* Excerpt */}
              {post.excerpt && (
                <p className="text-xl text-purple-100 leading-relaxed mb-6 max-w-4xl">
                  {post.excerpt}
                </p>
              )}

              {/* Meta Information */}
              <div className="flex flex-wrap items-center gap-6 text-sm text-purple-200">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span className="font-medium">{post.author?.name || 'Anonymous'}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <time dateTime={post.publishedAt || post.createdAt} className="font-medium">
                    {new Date(post.publishedAt || post.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </time>
                </div>

                {readingTime > 0 && (
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    <span className="font-medium">{readingTime} min read</span>
                  </div>
                )}

                <button
                  onClick={handleShare}
                  className="flex items-center gap-2 hover:text-white transition-colors font-medium bg-white/20 px-3 py-1 rounded-full"
                >
                  <Share2 className="h-4 w-4" />
                  <span>Share</span>
                </button>
              </div>
            </div>

            {/* Featured Image */}
            {post.featuredImage && (
              <div className="aspect-video overflow-hidden">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Content */}
            <div className="p-8">
              <div
                className="prose prose-lg max-w-none 
                  prose-headings:text-gray-800 prose-headings:font-bold prose-headings:scroll-mt-20
                  prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl prose-h4:text-lg
                  prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
                  prose-a:text-purple-600 prose-a:font-medium hover:prose-a:text-purple-800 prose-a:no-underline hover:prose-a:underline
                  prose-strong:text-gray-800 prose-strong:font-semibold
                  prose-ul:text-gray-700 prose-ol:text-gray-700 prose-li:text-gray-700 prose-li:mb-2
                  prose-blockquote:border-l-4 prose-blockquote:border-purple-500 prose-blockquote:bg-purple-50 prose-blockquote:p-4 prose-blockquote:text-gray-700 prose-blockquote:italic
                  prose-code:bg-gray-100 prose-code:text-gray-800 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm
                  prose-pre:bg-gray-100 prose-pre:text-gray-800 prose-pre:p-4 prose-pre:rounded-lg prose-pre:overflow-x-auto
                  prose-hr:border-gray-300
                  prose-table:text-gray-700 prose-thead:text-gray-800 prose-tbody:text-gray-700
                  [&>*]:text-gray-700 [&_p]:text-gray-700 [&_li]:text-gray-700 [&_span]:text-gray-700"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHTML(post.content)
                }}
              />
            </div>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="px-8 pb-8">
                <h3 className="text-lg font-semibold mb-4 text-gray-800">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 text-sm font-medium"
                    >
                      <Tag className="h-3 w-3 mr-1" />
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Author Bio */}
          {post.author && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mt-8">
              <div className="flex items-start gap-4">
                {post.author.avatar && (
                  <img
                    src={post.author.avatar}
                    alt={post.author.name}
                    className="w-16 h-16 rounded-full border-4 border-purple-100"
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-gray-800">About {post.author.name}</h3>
                    {post.author.title && (
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm font-medium rounded-full">
                        {post.author.title}
                      </span>
                    )}
                  </div>
                  {post.author.bio && (
                    <p className="text-gray-600 leading-relaxed">{post.author.bio}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Call to Action */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl shadow-xl text-white p-8 mt-8 text-center">
            <h3 className="text-3xl font-bold mb-4">
              Ready to streamline your dental practice administration?
            </h3>
            <p className="text-purple-100 mb-8 max-w-2xl mx-auto text-lg leading-relaxed">
              Join thousands of dental professionals - hygienists, therapists, nurses, and locums - who trust our 
              invoice management and expense tracking system to handle their billing efficiently and professionally.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/pricing"
                className="px-8 py-4 bg-white text-purple-600 rounded-xl hover:bg-gray-100 font-semibold text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
              >
                View Pricing Plans
              </Link>
              <Link
                to="/features"
                className="px-8 py-4 border-2 border-white text-white rounded-xl hover:bg-white hover:text-purple-600 font-semibold text-lg transition-all transform hover:scale-105"
              >
                Learn More
              </Link>
            </div>
          </div>

          {/* Navigation */}
          <div className="mt-12 pt-8 border-t-2 border-gray-200">
            <div className="flex justify-between items-center">
              <Link
                to="/blog"
                className="flex items-center text-purple-600 hover:text-purple-800 font-semibold text-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                All Blog Posts
              </Link>
              
              <div className="text-center">
                <Link
                  to="/blog"
                  className="text-gray-600 hover:text-purple-600 transition-colors font-medium"
                >
                  Browse more articles
                </Link>
              </div>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogPost;
